'use client';
import { useState, useEffect, useMemo } from 'react';
import {
	useGetManagementPlans,
	useGetPublicPlans,
	useGetTestimonials,
} from '@practium/features/api';
import { fuzzyFilter } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import type { LandingPlanItem, LandingTab } from '../types/landing.types';
import { LANDING_PLAN_COLORS, LANDING_PLAN_FALLBACK_COLORS } from '../constants/charts.constants';

const PAGE_SIZE = 10;
const LANDING_PLAN_LIMIT = 3;

const LANDING_FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Tipo',
		options: [
			{ value: 'tipo:Consultor', label: 'Consultor' },
			{ value: 'tipo:Agricultor', label: 'Agricultor' },
			{ value: 'tipo:Gestor', label: 'Gestor' },
		],
	},
	{
		label: 'Status',
		options: [
			{ value: 'status:active', label: 'Ativo' },
			{ value: 'status:inactive', label: 'Inativo' },
		],
	},
];

export const useLandingPage = () => {
	const [activeTab, setActiveTabState] = useState<LandingTab>('plans');
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);
	const testimonialsQuery = useGetTestimonials({ limit: 200, enabled: activeTab === 'testimonials' });
	const landingPlansQuery = useGetPublicPlans(activeTab === 'plans');
	const managementPlansQuery = useGetManagementPlans({
		limit: 1000,
		enabled: activeTab === 'plans',
	});

	const landingPlans = useMemo((): LandingPlanItem[] => {
		const plansById = new Map(managementPlansQuery.data.map(plan => [plan._id, plan]));

		return (landingPlansQuery.data ?? []).map(landingPlan => {
			const plan = plansById.get(landingPlan.plan._id);
			const hierarchy =
				typeof plan?.hierarchy === 'object'
					? plan.hierarchy.name
					: plan?.hierarchy ?? '-';

			return {
				_id: landingPlan._id,
				planId: landingPlan.plan._id,
				landingPlanId: landingPlan._id,
				name: plan?.name ?? landingPlan.plan.name,
				hierarchy,
				status: plan?.status === 'inactive' ? 'inactive' : 'active',
				price: plan?.price ?? landingPlan.plan.price,
				linkedUsers: plan?.linkedUsers ?? 0,
			};
		});
	}, [landingPlansQuery.data, managementPlansQuery.data]);

	const planDistribution = useMemo(
		() =>
			landingPlans.map((plan, index) => ({
				name: plan.name,
				value: plan.linkedUsers,
				color:
					LANDING_PLAN_COLORS[plan.name.trim().toLowerCase()] ??
					LANDING_PLAN_FALLBACK_COLORS[index % LANDING_PLAN_FALLBACK_COLORS.length],
			})),
		[landingPlans]
	);

	const availablePlans = useMemo(() => {
		const linkedPlanIds = new Set((landingPlansQuery.data ?? []).map(item => item.plan._id));
		if (linkedPlanIds.size >= LANDING_PLAN_LIMIT) return [];
		return managementPlansQuery.data.filter(
			plan => plan.status === 'active' && !linkedPlanIds.has(plan._id)
		);
	}, [landingPlansQuery.data, managementPlansQuery.data]);
	const hasReachedPlanLimit = (landingPlansQuery.data?.length ?? 0) >= LANDING_PLAN_LIMIT;

	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterValues]);

	const setActiveTab = (tab: LandingTab) => {
		setActiveTabState(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const statusFilters = filterValues.filter(v => v.startsWith('status:')).map(v => v.slice(7));
	const tipoFilters = filterValues.filter(v => v.startsWith('tipo:')).map(v => v.slice(5));

	const processedPlans = useMemo(() => {
		let result = fuzzyFilter(
			landingPlans,
			searchQuery,
			plan => [plan.name, plan.hierarchy].filter(Boolean).join(' ')
		);
		if (statusFilters.length > 0) result = result.filter(p => statusFilters.includes(p.status));
		if (tipoFilters.length > 0) {
			result = result.filter(plan =>
				tipoFilters.some(tipo => plan.hierarchy.toLowerCase().includes(tipo.toLowerCase()))
			);
		}
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [landingPlans, searchQuery, filterValues, sortAsc]);

	const processedTestimonials = useMemo(() => {
		let result = fuzzyFilter(testimonialsQuery.data, searchQuery, t =>
			[t.name, t.occupation].filter(Boolean).join(' ')
		);
		if (statusFilters.length > 0) {
			result = result.filter(t => statusFilters.includes(t.status ? 'active' : 'inactive'));
		}
		if (tipoFilters.length > 0) result = result.filter(t => t.occupation && tipoFilters.includes(t.occupation));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [testimonialsQuery.data, searchQuery, filterValues, sortAsc]);

	const totalPages = useMemo(() => {
		const len = activeTab === 'plans' ? processedPlans.length : processedTestimonials.length;
		return Math.max(1, Math.ceil(len / PAGE_SIZE));
	}, [activeTab, processedPlans.length, processedTestimonials.length]);

	const filteredPlans = useMemo(
		() => processedPlans.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedPlans, page]
	);

	const filteredTestimonials = useMemo(
		() => processedTestimonials.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedTestimonials, page]
	);

	return {
		activeTab,
		setActiveTab,
		availablePlans,
		planDistribution,
		hasReachedPlanLimit,
		filteredPlans,
		filteredTestimonials,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups: LANDING_FILTER_GROUPS,
		page,
		setPage,
		totalPages,
		isLoading:
			activeTab === 'plans'
				? landingPlansQuery.isLoading || managementPlansQuery.isLoading
				: testimonialsQuery.isLoading,
		isPlanError:
			activeTab === 'plans' && (landingPlansQuery.isError || managementPlansQuery.isError),
		planError: landingPlansQuery.error ?? managementPlansQuery.error,
		refetchPlans: async () => {
			await Promise.all([landingPlansQuery.refetch(), managementPlansQuery.refetch()]);
		},
	};
};
